import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { catchError, fromEvent, map, of, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SelectorDialogComponent, SelectorDialogDataRow } from 'src/app/components/main/shared/selector-dialog/selector-dialog.component';
import { RoleSlugEnum } from 'src/app/enums/role-slug.enum';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { UserSlackChatSlot } from 'src/app/models/user-slack-chat-slot.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'app-user-detail-chats-section',
  templateUrl: './user-detail-chats-section.component.html',
  styleUrl: './user-detail-chats-section.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon],
})
export class UserDetailChatsSectionComponent {
  private readonly api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly employee = inject(EmployeeService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  readonly user_id = input.required<string>();
  readonly user = input.required<User>();
  readonly its_me = input.required<boolean>();

  private readonly refresh_tick = signal(0);
  readonly connecting_team_id = signal<string | null>(null);
  readonly disconnecting_id = signal<number | null>(null);
  readonly assigning_google = signal(false);
  readonly assigning_teams = signal(false);

  readonly can_view = computed((): boolean => this.its_me() || this.permissions.validateDepartment(['direction', 'rrhh']));
  readonly can_connect = computed((): boolean => this.its_me());
  readonly can_disconnect = computed((): boolean => this.its_me() || this.permissions.validateDepartment(['direction', 'rrhh']));
  readonly can_manage_personal_chats = computed(
    (): boolean => this.permissions.validateDepartment(['direction', 'rrhh']) || this.permissions.isAccountManager()
  );
  readonly is_worker_user = computed((): boolean => this.user().role?.slug === RoleSlugEnum.WORKER);

  readonly slots = toSignal(
    toObservable(this.refresh_tick).pipe(
      switchMap(() =>
        this.api.getUserChatSlackSlots(this.user_id()).pipe(
          map((slots: UserSlackChatSlot[]): UserSlackChatSlotView[] =>
            slots.map(
              (slot: UserSlackChatSlot): UserSlackChatSlotView => ({
                ...slot,
                product_titles_label: slot.product_titles.length > 0 ? slot.product_titles.join(', ') : null,
                display_logo_url: slot.product_logo_url || '/img/default-company_product.png',
              })
            )
          ),
          catchError(() => of([] as UserSlackChatSlotView[]))
        )
      )
    ),
    { initialValue: [] as UserSlackChatSlotView[] }
  );

  readonly google_conversation = toSignal(
    toObservable(this.refresh_tick).pipe(
      switchMap(() =>
        this.api.getUserMessagingConversation(this.user_id(), { provider: 'google_chat' }).pipe(
          map((conversation: MessagingConversation | null): MessagingConversation | null =>
            conversation != null && typeof conversation.id === 'number' ? conversation : null
          ),
          catchError(() => of(null as MessagingConversation | null))
        )
      )
    ),
    { initialValue: null as MessagingConversation | null }
  );

  readonly teams_conversation = toSignal(
    toObservable(this.refresh_tick).pipe(
      switchMap(() =>
        this.api.getUserMessagingConversation(this.user_id(), { provider: 'teams' }).pipe(
          map((conversation: MessagingConversation | null): MessagingConversation | null =>
            conversation != null && typeof conversation.id === 'number' ? conversation : null
          ),
          catchError(() => of(null as MessagingConversation | null))
        )
      )
    ),
    { initialValue: null as MessagingConversation | null }
  );

  readonly google_conversation_label = computed((): string => this.conversationLabel(this.google_conversation(), 'google'));
  readonly teams_conversation_label = computed((): string => this.conversationLabel(this.teams_conversation(), 'teams'));

  readonly connected_slots_count = computed(
    (): number => this.slots().filter((slot: UserSlackChatSlotView): boolean => slot.is_connected).length
  );
  readonly connectable_slots_count = computed(
    (): number => this.slots().filter((slot: UserSlackChatSlotView): boolean => !slot.needs_admin_configuration).length
  );

  constructor() {
    fromEvent(window, 'focus')
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        filter((): boolean => this.connecting_team_id() !== null)
      )
      .subscribe((): void => {
        this.connecting_team_id.set(null);
        this.refresh_slots();
      });
  }

  connect_slack(slot: UserSlackChatSlotView): void {
    if (
      !this.can_connect() ||
      this.connecting_team_id() !== null ||
      slot.is_connected ||
      slot.needs_admin_configuration ||
      slot.team_id == null
    ) {
      return;
    }

    this.connecting_team_id.set(slot.team_id);
    this.api.startSlackChatOAuth(slot.team_id).subscribe({
      next: (response): void => {
        window.open(response.authorization_url, '_blank', 'noopener,noreferrer');
      },
      error: (): void => {
        this.connecting_team_id.set(null);
      },
    });
  }

  refresh_slots(): void {
    this.refresh_tick.update((tick: number): number => tick + 1);
    this.employee.refreshActionsRequired();
  }

  disconnect(slot: UserSlackChatSlotView): void {
    if (!this.can_disconnect() || this.disconnecting_id() !== null || slot.connection_id === null) {
      return;
    }

    const label = slot.workspace_name ?? slot.company_name;
    if (!confirm(`¿Desconectar Slack workspace «${label}»?`)) {
      return;
    }

    this.disconnecting_id.set(slot.connection_id);
    this.api.deleteUserChatOAuthConnection(slot.connection_id).subscribe({
      next: (): void => {
        this.disconnecting_id.set(null);
        this.refresh_slots();
      },
      error: (): void => {
        this.disconnecting_id.set(null);
      },
    });
  }

  assign_google_conversation(): void {
    if (!this.can_manage_personal_chats() || this.assigning_google()) {
      return;
    }

    this.assigning_google.set(true);
    this.api
      .getMessagingConversations({
        scopes: 'pendingAssignment',
        google_chat_space_type: 'DIRECT_MESSAGE',
        provider: 'google_chat',
        per_page: 200,
        order_by: 'display_name',
      })
      .subscribe({
        next: response => {
          this.assigning_google.set(false);
          const rows: SelectorDialogDataRow[] = response.data.map(
            (conversation: MessagingConversation): SelectorDialogDataRow => ({
              value: conversation.id,
              textToShow: `${conversation.display_name} (${conversation.google_chat_space_name ?? ''})`,
            })
          );

          this.openAssignDialog('Asignar conversación de Google Chat', rows);
        },
        error: (): void => {
          this.assigning_google.set(false);
        },
      });
  }

  unassign_google_conversation(): void {
    const conversation = this.google_conversation();
    if (!this.can_manage_personal_chats() || conversation == null) {
      return;
    }

    this.confirmUnassign(conversation.id, this.google_conversation_label());
  }

  assign_teams_conversation(): void {
    if (!this.can_manage_personal_chats() || this.assigning_teams()) {
      return;
    }

    this.assigning_teams.set(true);
    this.api
      .getMessagingConversations({
        scopes: 'pendingAssignment,teamsBotInstalled',
        provider: 'teams',
        teams_conversation_type: 'one_on_one',
        per_page: 200,
        order_by: 'display_name',
      })
      .subscribe({
        next: response => {
          this.assigning_teams.set(false);
          const rows: SelectorDialogDataRow[] = response.data.map(
            (conversation: MessagingConversation): SelectorDialogDataRow => ({
              value: conversation.id,
              textToShow: this.teamsPendingOptionLabel(conversation),
            })
          );

          this.openAssignDialog('Asignar conversación de Microsoft Teams', rows);
        },
        error: (): void => {
          this.assigning_teams.set(false);
        },
      });
  }

  unassign_teams_conversation(): void {
    const conversation = this.teams_conversation();
    if (!this.can_manage_personal_chats() || conversation == null) {
      return;
    }

    this.confirmUnassign(conversation.id, this.teams_conversation_label());
  }

  private openAssignDialog(dialog_title: string, rows: SelectorDialogDataRow[]): void {
    this.dialog
      .open(SelectorDialogComponent, {
        width: '520px',
        data: {
          dialog_title,
          dialog_ok_button: 'Asignar',
          dialog_cancel_button: 'Cancelar',
          data: rows,
        },
      })
      .afterClosed()
      .subscribe((conversation_id: number | undefined): void => {
        if (conversation_id == null) {
          return;
        }

        this.api
          .assignMessagingConversationToUser(conversation_id, {
            user_id: Number(this.user_id()),
          })
          .subscribe((): void => {
            this.refresh_slots();
          });
      });
  }

  private confirmUnassign(conversation_id: number, label: string): void {
    if (!confirm(`¿Quitar la asignación de «${label}» de este usuario? La conversación volverá a quedar pendiente.`)) {
      return;
    }

    this.api.unassignMessagingConversation(conversation_id).subscribe((): void => {
      this.refresh_slots();
    });
  }

  private conversationLabel(conversation: MessagingConversation | null, provider: 'google' | 'teams'): string {
    if (conversation == null) {
      return '';
    }

    const display_name = conversation.display_name?.trim() ?? '';
    if (display_name !== '') {
      return display_name;
    }

    if (provider === 'google') {
      const space_name = conversation.google_chat_space_name?.trim() ?? '';
      if (space_name !== '') {
        return space_name;
      }
    }

    if (provider === 'teams') {
      const channel_id = conversation.teams_channel_id?.trim() ?? '';
      if (channel_id !== '') {
        return channel_id;
      }
    }

    return `Conversación #${conversation.id}`;
  }

  private teamsPendingOptionLabel(conversation: MessagingConversation): string {
    const display_name = conversation.display_name?.trim() || `Conversación #${conversation.id}`;
    const tenant = conversation.teams_tenant_id?.trim();

    return tenant ? `${display_name} (${tenant})` : display_name;
  }
}

interface UserSlackChatSlotView extends UserSlackChatSlot {
  product_titles_label: string | null;
  display_logo_url: string;
}
